import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export interface CaseDeadline {
  id: string;

  caseId: string;
  caseTitle: string | null;

  clientId: string | null;
  clientName: string | null;

  legalDeadlineId: string | null;
  legalDeadlineName: string | null;

  title: string;

  ruleNameSnapshot: string | null;
  legalBasisSnapshot: string | null;

  daysCountSnapshot: number | null;
  countTypeSnapshot: string | null;
  startTermSnapshot: string | null;

  startDate: string | null;

  suggestedDueDate: string | null;
  dueDate: string | null;

  sourceType: string;
  sourceId: string | null;

  taskId: string | null;
  taskTitle: string | null;

  status: string;
  riskLevel: string;

  responsibleUser: string | null;

  completedAt: string | null;
  notes: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface CreateCaseDeadlineInput {
  caseId: string;

  legalDeadlineId?:
    string |
    null;

  title?:
    string |
    null;

  startDate?:
    string |
    null;

  suggestedDueDate?:
    string |
    null;

  dueDate?:
    string |
    null;

  sourceType?:
    string |
    null;

  sourceId?:
    string |
    null;

  taskId?:
    string |
    null;

  status?:
    string;

  riskLevel?:
    string;

  responsibleUser?:
    string |
    null;

  completedAt?:
    string |
    null;

  notes?:
    string |
    null;

  createTask?:
    boolean;
}

export interface UpdateCaseDeadlineInput {
  title?:
    string;

  startDate?:
    string |
    null;

  suggestedDueDate?:
    string |
    null;

  dueDate?:
    string |
    null;

  status?:
    string;

  riskLevel?:
    string;

  responsibleUser?:
    string |
    null;

  completedAt?:
    string |
    null;

  notes?:
    string |
    null;

  createTask?:
    boolean;
}

export interface CaseDeadlineFilters {
  caseId?:
    string;

  status?:
    string;

  dueFrom?:
    string;

  dueTo?:
    string;

  includeArchived?:
    boolean;
}

type UseCaseDeadlinesOptions = {
  autoLoad?:
    boolean;

  caseId?:
    string;
};

const nullableText =
  (
    value: unknown
  ) => {
    if (
      value === undefined ||
      value === null
    ) {
      return null;
    }

    const text =
      String(
        value
      ).trim();

    return text ||
      null;
  };

const nullableNumber =
  (
    value: unknown
  ) => {
    if (
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return null;
    }

    const number =
      Number(
        value
      );

    return Number.isFinite(
      number
    )
      ? number
      : null;
  };

const normalizeCaseDeadline =
  (
    value: any
  ):
    CaseDeadline => ({
    id:
      String(
        value?.id ??
        ''
      ),

    caseId:
      String(
        value?.caseId ??
        value?.case_id ??
        ''
      ),

    caseTitle:
      nullableText(
        value?.caseTitle ??
        value?.case_title
      ),

    clientId:
      nullableText(
        value?.clientId ??
        value?.client_id
      ),

    clientName:
      nullableText(
        value?.clientName ??
        value?.client_name
      ),

    legalDeadlineId:
      nullableText(
        value?.legalDeadlineId ??
        value?.legal_deadline_id
      ),

    legalDeadlineName:
      nullableText(
        value?.legalDeadlineName ??
        value?.legal_deadline_name
      ),

    title:
      String(
        value?.title ??
        ''
      ).trim(),

    ruleNameSnapshot:
      nullableText(
        value?.ruleNameSnapshot ??
        value?.rule_name_snapshot
      ),

    legalBasisSnapshot:
      nullableText(
        value?.legalBasisSnapshot ??
        value?.legal_basis_snapshot
      ),

    daysCountSnapshot:
      nullableNumber(
        value?.daysCountSnapshot ??
        value?.days_count_snapshot
      ),

    countTypeSnapshot:
      nullableText(
        value?.countTypeSnapshot ??
        value?.count_type_snapshot
      ),

    startTermSnapshot:
      nullableText(
        value?.startTermSnapshot ??
        value?.start_term_snapshot
      ),

    startDate:
      nullableText(
        value?.startDate ??
        value?.start_date
      ),

    suggestedDueDate:
      nullableText(
        value?.suggestedDueDate ??
        value?.suggested_due_date
      ),

    dueDate:
      nullableText(
        value?.dueDate ??
        value?.due_date
      ),

    sourceType:
      nullableText(
        value?.sourceType ??
        value?.source_type
      ) ??
      'manual',

    sourceId:
      nullableText(
        value?.sourceId ??
        value?.source_id
      ),

    taskId:
      nullableText(
        value?.taskId ??
        value?.task_id
      ),

    taskTitle:
      nullableText(
        value?.taskTitle ??
        value?.task_title
      ),

    status:
      nullableText(
        value?.status
      ) ??
      'pending',

    riskLevel:
      nullableText(
        value?.riskLevel ??
        value?.risk_level
      ) ??
      'medium',

    responsibleUser:
      nullableText(
        value?.responsibleUser ??
        value?.responsible_user
      ),

    completedAt:
      nullableText(
        value?.completedAt ??
        value?.completed_at
      ),

    notes:
      nullableText(
        value?.notes
      ),

    createdAt:
      nullableText(
        value?.createdAt ??
        value?.created_at
      ) ??
      new Date()
        .toISOString(),

    updatedAt:
      nullableText(
        value?.updatedAt ??
        value?.updated_at
      ) ??
      new Date()
        .toISOString(),
  });

const sortCaseDeadlines =
  (
    left:
      CaseDeadline,

    right:
      CaseDeadline
  ) => {
    if (
      left.dueDate &&
      right.dueDate
    ) {
      const dateDifference =
        left.dueDate.localeCompare(
          right.dueDate
        );

      if (
        dateDifference !==
        0
      ) {
        return dateDifference;
      }
    }

    if (
      left.dueDate &&
      !right.dueDate
    ) {
      return -1;
    }

    if (
      !left.dueDate &&
      right.dueDate
    ) {
      return 1;
    }

    return right.createdAt.localeCompare(
      left.createdAt
    );
  };

export function useCaseDeadlines(
  options:
    UseCaseDeadlinesOptions =
    {}
) {
  const {
    autoLoad =
      true,

    caseId,
  } = options;

  const [
    caseDeadlines,
    setCaseDeadlines,
  ] =
    useState<
      CaseDeadline[]
    >(
      []
    );

  const [
    isLoadingCaseDeadlines,
    setIsLoadingCaseDeadlines,
  ] =
    useState(
      false
    );

  const [
    isSavingCaseDeadline,
    setIsSavingCaseDeadline,
  ] =
    useState(
      false
    );

  const [
    caseDeadlinesError,
    setCaseDeadlinesError,
  ] =
    useState<
      string |
      null
    >(
      null
    );

  const loadCaseDeadlines =
    useCallback(
      async (
        filters:
          CaseDeadlineFilters =
          {}
      ) => {
        setIsLoadingCaseDeadlines(
          true
        );

        setCaseDeadlinesError(
          null
        );

        try {
          const params =
            new URLSearchParams();

          const resolvedCaseId =
            filters.caseId ??
            caseId;

          if (
            resolvedCaseId?.trim()
          ) {
            params.set(
              'caseId',
              resolvedCaseId.trim()
            );
          }

          if (
            filters.status?.trim()
          ) {
            params.set(
              'status',
              filters.status.trim()
            );
          }

          if (
            filters.dueFrom?.trim()
          ) {
            params.set(
              'dueFrom',
              filters.dueFrom.trim()
            );
          }

          if (
            filters.dueTo?.trim()
          ) {
            params.set(
              'dueTo',
              filters.dueTo.trim()
            );
          }

          if (
            filters.includeArchived
          ) {
            params.set(
              'includeArchived',
              '1'
            );
          }

          const query =
            params.toString();

          const response =
            await fetchWithRetry(
              `/api/case-deadlines${
                query
                  ? `?${query}`
                  : ''
              }`
            );

          const data =
            await response
              .json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.caseDeadlines
            )
          ) {
            throw new Error(
              data.error ||
              `GET /api/case-deadlines falhou com status ${response.status}`
            );
          }

          const loaded =
            data.caseDeadlines
              .map(
                normalizeCaseDeadline
              )
              .sort(
                sortCaseDeadlines
              );

          setCaseDeadlines(
            loaded
          );

          return loaded;
        } catch (
          error
        ) {
          const message =
            error instanceof Error
              ? error.message
              : 'Não foi possível carregar os prazos operacionais.';

          setCaseDeadlinesError(
            message
          );

          throw error;
        } finally {
          setIsLoadingCaseDeadlines(
            false
          );
        }
      },
      [
        caseId,
      ]
    );

  const createCaseDeadline =
    useCallback(
      async (
        input:
          CreateCaseDeadlineInput
      ) => {
        setIsSavingCaseDeadline(
          true
        );

        setCaseDeadlinesError(
          null
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/case-deadlines',
              {
                method:
                  'POST',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body:
                  JSON.stringify(
                    input
                  ),
              }
            );

          const data =
            await response
              .json();

          if (
            !response.ok ||
            !data.success ||
            !data.caseDeadline
          ) {
            throw new Error(
              data.error ||
              `POST /api/case-deadlines falhou com status ${response.status}`
            );
          }

          const created =
            normalizeCaseDeadline(
              data.caseDeadline
            );

          setCaseDeadlines(
            (
              current
            ) =>
              [
                created,

                ...current.filter(
                  (
                    item
                  ) =>
                    item.id !==
                    created.id
                ),
              ].sort(
                sortCaseDeadlines
              )
          );

          return created;
        } catch (
          error
        ) {
          const message =
            error instanceof Error
              ? error.message
              : 'Não foi possível criar o prazo operacional.';

          setCaseDeadlinesError(
            message
          );

          throw error;
        } finally {
          setIsSavingCaseDeadline(
            false
          );
        }
      },
      []
    );

  const updateCaseDeadline =
    useCallback(
      async (
        id:
          string,

        input:
          UpdateCaseDeadlineInput
      ) => {
        setIsSavingCaseDeadline(
          true
        );

        setCaseDeadlinesError(
          null
        );

        try {
          const response =
            await fetchWithRetry(
              `/api/case-deadlines/${encodeURIComponent(
                id
              )}`,
              {
                method:
                  'PUT',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body:
                  JSON.stringify(
                    input
                  ),
              }
            );

          const data =
            await response
              .json();

          if (
            !response.ok ||
            !data.success ||
            !data.caseDeadline
          ) {
            throw new Error(
              data.error ||
              `PUT /api/case-deadlines/${id} falhou com status ${response.status}`
            );
          }

          const updated =
            normalizeCaseDeadline(
              data.caseDeadline
            );

          setCaseDeadlines(
            (
              current
            ) =>
              [
                ...current.filter(
                  (
                    item
                  ) =>
                    item.id !==
                    updated.id
                ),

                updated,
              ].sort(
                sortCaseDeadlines
              )
          );

          return updated;
        } catch (
          error
        ) {
          const message =
            error instanceof Error
              ? error.message
              : 'Não foi possível atualizar o prazo operacional.';

          setCaseDeadlinesError(
            message
          );

          throw error;
        } finally {
          setIsSavingCaseDeadline(
            false
          );
        }
      },
      []
    );

  const archiveCaseDeadline =
    useCallback(
      async (
        id:
          string
      ) => {
        setIsSavingCaseDeadline(
          true
        );

        setCaseDeadlinesError(
          null
        );

        try {
          const response =
            await fetchWithRetry(
              `/api/case-deadlines/${encodeURIComponent(
                id
              )}`,
              {
                method:
                  'DELETE',
              }
            );

          const data =
            await response
              .json();

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              `DELETE /api/case-deadlines/${id} falhou com status ${response.status}`
            );
          }

          setCaseDeadlines(
            (
              current
            ) =>
              current.filter(
                (
                  item
                ) =>
                  item.id !==
                  id
              )
          );
        } catch (
          error
        ) {
          const message =
            error instanceof Error
              ? error.message
              : 'Não foi possível arquivar o prazo operacional.';

          setCaseDeadlinesError(
            message
          );

          throw error;
        } finally {
          setIsSavingCaseDeadline(
            false
          );
        }
      },
      []
    );

  useEffect(
    () => {
      if (
        !autoLoad
      ) {
        return;
      }

      void loadCaseDeadlines();
    },
    [
      autoLoad,
      loadCaseDeadlines,
    ]
  );

  return {
    caseDeadlines,

    isLoadingCaseDeadlines,
    isSavingCaseDeadline,

    caseDeadlinesError,

    loadCaseDeadlines,
    createCaseDeadline,
    updateCaseDeadline,
    archiveCaseDeadline,

    setCaseDeadlines,
  };
}